import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectors } from "src/redux/Toaster";
import { ToasterActions } from "src/redux/Toaster/actions";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import NotificationInfoIcon from "src/asset/notification-info.svg";
import NotificationOKIcon from "src/asset/notification-ok.svg";
import NotificationWarningIcon from "src/asset/notification-warning.svg";
import NotificationErrorIcon from "src/asset/notification-error.svg";
import NotificationCloseIcon from "src/asset/notification-close.svg";
import { alertTaskbar, stopTaskbarBlinking } from "src/utils";
import styles from "./styles.module.scss";
import isEmpty from "is-empty";
import Humanize from "humanize-plus";

export default () => {
    const allToasts = useSelector(selectors.Get);
    // Limited UI: spec §3 "Toast Notification | Disabled". Suppress
    // every non-error toast (trade events, info, warning, OK success).
    // Errors stay visible so real failures (auth, API, validation)
    // still surface — silent failure modes are worse than noise.
    const limitedUi =
        useSelector(SettingsSelectors.FeatureFlags)["limited-ui"] === true;
    const toasts = limitedUi
        ? allToasts.filter((t) => t.appearance === "error")
        : allToasts;

    if (toasts.length === 0) return null;

    return (
        <div className={styles.wrapper}>
            {toasts.map((d) => (
                <Toast key={d.id} data={d} />
            ))}
        </div>
    );
};

const icons = Object.freeze({
    info: NotificationInfoIcon,
    ok: NotificationOKIcon,
    warning: NotificationWarningIcon,
    error: NotificationErrorIcon,
});

const Toast = ({ data }) => {
    const {
        id,
        appearance,
        title,
        message,
        stick,
        count,
        cpty,
        pushType,
        details,
    } = data;

    const dispatch = useDispatch();
    const [msg, setMessage] = useState(null);
    const classes = [styles.toast, styles[appearance]];
    const icon = icons[appearance];

    const closeToaster = useCallback(() => {
        dispatch(ToasterActions.Remove(id));
        stopTaskbarBlinking();
    }, [dispatch, id]);

    useEffect(() => {
        if (stick === 0) return;
        const timer = setTimeout(closeToaster, stick);
        return () => clearTimeout(timer);
    }, [stick, closeToaster]);

    useEffect(() => {
        const browserMsg = getBrowserMessage(message, pushType, count, cpty);
        const inAppMsg = getInAppMessage(browserMsg, pushType, details);
        if (["request", "offer", "availability", "recall"].includes(pushType)) {
            nativeNotify(title, browserMsg);
        }
        setMessage(inAppMsg);
        alertTaskbar(browserMsg);
    }, [title, message, pushType, count, cpty, details]);

    return msg ? (
        <div className={classes.join(" ")}>
            <img
                className={styles.close}
                src={NotificationCloseIcon}
                alt="close"
                data-testid={`close-toaster-${pushType || "none"}`}
                onClick={closeToaster}
            />
            <img className={styles.icon} src={icon} alt={appearance} />
            <div
                className={styles.content}
                data-testid={`content-toaster-${pushType || "none"}`}
            >
                <h1>{title}</h1>
                <div>{msg}</div>
            </div>
        </div>
    ) : (
        <div />
    );
};

const pushTypeMessages = Object.freeze({
    offer: "Received Offers from",
    recall: "Received a Recall from",
    recallCanceled: "Recalls canceled by",
    recallDisputed: "Recalls disputed by",
    request: "Received Loan Requests from",
    match: "Auto-Matched Loans with",
    accept: "Offers Accepted by",
    reject: "Rejections from",
    fork: "EAs Forked by",
    upgrade: "EAs upgraded by",
    release: "EAs released by",
    confirm: "Confirmed by",
    canceled: "Canceled by",
    modified: "Modified by",
    availability: "New inbox messages from",
    upgrade_request: "New EAs Upgrade Request from",
});

const getBrowserMessage = (message, pushType, count, cpty) => {
    if (pushType && cpty) {
        //bit hacky, but it's where we've got to
        const cptyStr = cpty === "blank" ? "" : cpty;
        const out = `${pushTypeMessages[pushType]} ${cptyStr}: ${count || ""}`;
        return out;
    }

    return message;
};

const getInAppMessage = (message, pushType, details) => {
    if (pushType === "recall" && !isEmpty(details)) {
        return (
            <>
                <div>{message}</div>
                <table className={styles.recallDetails}>
                    <thead>
                        <tr>
                            <th>Security</th>
                            <th>Quantity</th>
                            <th>Due Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{details.security?.name}</td>
                            <td>{Humanize.intComma(details.qty)}</td>
                            <td>{details.dueDate}</td>
                        </tr>
                    </tbody>
                </table>
            </>
        );
    }

    return message;
};
const nativeNotify = (title, message) => {
    if (!("Notification" in window)) {
        return;
    }
    // Let's check whether notification permissions have already been granted
    if (Notification.permission === "granted") {
        // If it's okay let's create a notification
        //TODO we should probably keep track of notifications somewhere and close them if someone else responds
        new Notification(title, {
            body: message,
            badge: "favicon.png",
            icon: "favicon.png",
        });
    } else if (Notification.permission !== "denied") {
        // Otherwise, we need to ask the user for permission
        Notification.requestPermission(function (permission) {
            // If the user accepts, let's create a notification
            if (permission === "granted") {
                //TODO we should probably keep track of notifications somewhere and close them if someone else responds
                new Notification(title, {
                    body: message,
                    badge: "favicon.png",
                    icon: "favicon.png",
                });
            }
        });
    }
};
